import dayjs from 'dayjs';
import store from '../redux/store';
import { dayjsFormatDate } from '../Constants';
import { unassignedCategoryName } from './StructureUtils';
import { IFieldConfig } from '../redux/project/documents/sliceProjectDocuments';

/** Wether value is null or undefined. */
export const isNullOrUndefined = (value: unknown) => {
    return value === null || value === undefined;
};

/**
 * Parses a username received from HCL Notes.
 * @param username - Raw username string.
 */
export const parseUsername = (username: string) =>
    username?.indexOf('CN=') === 0 ? username.replace('CN=', '').split('/')[0] : username;

/**
 * Parses a date string received from HCL Notes.
 * @param value - Raw date string.
 */
export const parseDate = (value: number | string) => value && dayjs(new Date(value)).format('L');

/**
 * Simple string sorting function.
 * @param a - First object.
 * @param b - Second object.
 * @returns Sorting index.
 */
export const stringSort = (a: string, b: string) => {
    if (a === unassignedCategoryName) {
        return 1;
    }
    if (b === unassignedCategoryName) {
        return -1;
    }
    return a.localeCompare(b);
};

export const numberSort = (a: string, b: string) => {
    return parseInt(a) > parseInt(b) ? 1 : -1;
};

/**
 * Simple date sorting function.
 * @param a - First object.
 * @param b - Second object.
 * @returns Sorting index.
 */
export const dateSort = (a: string, b: string) =>
    dayjs(a, dayjsFormatDate).isAfter(dayjs(b, dayjsFormatDate)) ? 1 : -1;

/** RegExp for a url validation. */
const urlPattern = /^(http(s)):\/\/.[\w#%+.:=@~-]{2,256}\.[a-z]{2,6}\b([\w#%&+./:=?@~-])*$/u;

/**
 * Validates url.
 * @param url Url string.
 * @returns boolean true if valid url.
 */
export const isValidUrl = (url?: string): boolean => {
    if (!url) {
        return;
    }
    return urlPattern.test(url);
};

/** Function that checks the document type and returns the filtered document. */
export const GetIcon = (name: string, type: string) => {
    if (type.toLowerCase() === 'docstatus') {
        /** Access data from redux store. */
        const { projectDocStatus } = store.getState();
        return projectDocStatus.data?.filter(x => x.name === name)[0];
    } else {
        /** Access data from redux store. */
        const { projectDocTypes } = store.getState();
        return projectDocTypes.data?.filter(x => x.name === name)[0];
    }
};

/** Function that parses json safely. */
export const safeJsonParse = <T>(text: string, defaultValue: T): T => {
    try {
        return JSON.parse(text);
    } catch (error) {
        return defaultValue;
    }
};

/**
 * Formats a date string as MM/DD/YYYY hh:mm:ss.
 * L LTS example output: 08/16/2018 8:02:18
 * @param value - Raw date string.
 */
export const showDateTimeFormat = (value: number | string) => {
    return dayjs(new Date(value)).format('L LTS');
};

/**
 * Checks if field has a correct length.
 * @param param0 - Object with fieldValue and required length (from, to).
 * @returns string with error key or empty string, if length of the field is correct.
 */
export const checkTextFieldLength = ({
    fieldValue,
    from,
    to,
    optional,
}: {
    fieldValue: string;
    from: number;
    to: number;
    optional: boolean;
}): string => {
    if (isNullOrUndefined(fieldValue)) {
        return optional ? '' : 'lengthError';
    }
    return fieldValue.length < from || fieldValue.length > to ? 'lengthError' : '';
};

/**
 * Validates textfield value.
 * @param fieldValue - fieldValue string.
 * @returns string with error key or empty string, if the field is correct.
 */
export const checkTextFieldCharacters = (fieldValue: string, textfieldPattern?: string): string => {
    if (!textfieldPattern) return '';
    const formatted = textfieldPattern.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
    const reg = new RegExp(`^[^${formatted}]+$`, 'g');
    return reg.test(fieldValue) ? '' : 'characterError';
};

export const ensureArray = (value?: string | string[]): string[] => {
    if (!value) {
        return [];
    }
    return value instanceof Array ? value : [value];
};

export const moveInArray = <T>(arr: T, from: number, to: number): T => {
    if (!Array.isArray(arr)) return;
    const elemToMove = arr.splice(from, 1)[0];
    arr.splice(to, 0, elemToMove);
    return arr;
};

export const getAllFields = (fieldConfigs: IFieldConfig[]): string[] => {
    const fieldset = new Set<string>();

    fieldConfigs.forEach(config => {
        config.fields.forEach(fields => {
            fields.split(';').forEach(subfield => fieldset.add(subfield));
        });
    });

    return Array.from(fieldset);
};

/**
 * Checks if all elements in an array have the same value for a specified property.
 *
 * @template T - The type of objects in the array.
 * @param array - The array of objects to check.
 * @param property - The property to compare across all objects.
 * @returns `true` if all objects have the same value for the specified property, or `true` if the array is empty.
 */
export const allPropertiesSame = <T>(array: T[], property: keyof T): boolean =>
    array.length === 0 || array.every(item => item[property] === array[0][property]);

/**
 * Disable action in accordance with docs number
 * @param docsLength - Number of documents.
 * @param max - Max available number of docs.
 * @param min - Min available number of docs.
 * @returns - boolean, is active button disable.
 */
export const defineActionButtonsAvailability = (
    docsLength: number,
    max: number,
    min: number,
): boolean => (!!min && docsLength < min) || (!!max && docsLength > max);

/**
 * Define info toolTip if actions button is disabled.
 * @param maxDocs - Max available number of docs.
 * @param minDocs - Min available number of docs.
 * @returns - key for translations.
 */
export const getToolTip = (maxDocs: number, minDocs: number): string => {
    if (!!maxDocs && !!minDocs) return 'docsNumbersBetween';

    return maxDocs ? 'docsNumbersLimited' : 'docsNumbersNotEnough';
};

export const getMultilineFields = (fields?: IFieldConfig[]) =>
    (fields ?? []).filter(field => field.type === 'multiline').map(field => field.field);

export const matchesInOrder = (subArray: string[], mainArray: string[]): boolean => {
    if (subArray.length > mainArray.length) return false;

    let pos = 0;
    for (let i = 0; i < mainArray.length && pos < subArray.length; i++) {
        if (mainArray[i] === subArray[pos]) {
            pos++;
        }
    }

    return pos === subArray.length;
};
